import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../../../widgets/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../widgets/Navbars/DashboardNavbar";
import { AppButton, AppIconButton, AppTextField } from "../../../components/form";
import { PageCommandBar, PageSectionCard } from "../../../components/layout";
import { AppFormField, AppFormGrid, PageListContainer } from "../../../components/Page";
import { GRID_FULL } from "../../../lib/ui/formLayout";
import DateRangeHeader, { useDateRangeHeader } from "../../../components/DateRangeHeader";
import WidgetParametersEditor from "../../../components/WidgetParametersEditor";
import WidgetQuerySourcePanel from "../../../components/WidgetQuerySourcePanel";
import WidgetSourceDateRanges from "../../../components/WidgetSourceDateRanges";
import JsonCodeEditor from "../../../components/JsonCodeEditor";
import { dashboardQueryService } from "../../../services/dashboardQueryService";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import type { DashboardQuery } from "../../../types/query";
import type { WidgetDataSource } from "../../../types/widget";
import {
  applySourcesToDataSource,
  buildWidgetExecuteRequest,
  normalizeWidgetSources,
  syncSourceDateRanges,
  type SourceDateRange,
} from "../../../utils/widgetDataSources";
import { dashboardTimezone } from "../../../utils/dashboardDateDefaults";
import {
  DEFAULT_DSL,
  DEFAULT_PARAMETERS,
  emptyQuery,
  isValidJson,
  parseJsonField,
} from "./constants";

type Mode = "create" | "edit";

type QueryEditPageProps = {
  mode: Mode;
  embedded?: boolean;
  queryId?: string;
  onClose?: () => void;
  onSaved?: () => void;
};

export default function QueryEditPage({
  mode,
  embedded = false,
  queryId,
  onClose,
  onSaved,
}: QueryEditPageProps) {
  const { id: routeId } = useParams();
  const id = queryId ?? routeId;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const pageStyles = custom.pages.dashboardQueries;
  const editorStyles = custom.components.queryEditor;
  const date = useDateRangeHeader();
  const goBack = () => {
    if (onClose) onClose();
    else navigate("/widgets?tab=queries");
  };
  const [query, setQuery] = useState<DashboardQuery>(emptyQuery);
  const [dslText, setDslText] = useState(DEFAULT_DSL);
  const [queryBodyText, setQueryBodyText] = useState("SELECT 1 AS id");
  const [sources, setSources] = useState(emptyQuery().dataSource!.sources!);
  const [sourceDateRanges, setSourceDateRanges] = useState<SourceDateRange[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(mode === "edit");
  const [probeLoading, setProbeLoading] = useState(false);
  const [probeError, setProbeError] = useState<string | null>(null);
  const [probeResult, setProbeResult] = useState<string | null>(null);

  const pageTitle = mode === "create" ? t("query.create") : t("query.edit");
  const primaryIndex = useMemo(() => sources[0]?.index || "", [sources]);
  const adaptorSource = (query.dataSource?.sourceType ?? "OPENSEARCH") === "ADAPTOR";
  const multiSource = !adaptorSource && sources.length > 1;
  const dslJsonError = useMemo(() => !isValidJson(dslText), [dslText]);
  const probeReady = adaptorSource
    ? Boolean(query.dataSource?.adaptorId && queryBodyText.trim())
    : Boolean(primaryIndex.trim() && !dslJsonError);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const loaded = await dashboardQueryService.get(id);
      setQuery(loaded);
      const loadedSources = normalizeWidgetSources(loaded.dataSource);
      setSources(loadedSources.length ? loadedSources : []);
      setDslText(JSON.stringify(loaded.dataSource?.dslTemplate ?? {}, null, 2));
      setQueryBodyText(loaded.dataSource?.queryBody ?? "SELECT 1 AS id");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (mode === "edit") {
      load().catch(console.error);
    }
  }, [load, mode]);

  useEffect(() => {
    setSourceDateRanges((previous) => syncSourceDateRanges(previous, sources, date.range));
  }, [sources, date.range.from, date.range.to]);

  const buildDataSource = useCallback((): WidgetDataSource => {
    const sourceType = query.dataSource?.sourceType ?? "OPENSEARCH";
    const parameters =
      query.dataSource?.parameters?.length ? query.dataSource.parameters : DEFAULT_PARAMETERS;
    if (sourceType === "ADAPTOR") {
      return {
        ...query.dataSource,
        sourceType: "ADAPTOR",
        adaptorId: query.dataSource?.adaptorId,
        queryLanguage: query.dataSource?.queryLanguage ?? "SQL",
        queryBody: queryBodyText.trim(),
        parameters,
      };
    }
    return applySourcesToDataSource(
      {
        ...query.dataSource,
        sourceType: "OPENSEARCH",
        dslTemplate: parseJsonField(dslText, t("query.dsl")),
        parameters,
      },
      sources
    );
  }, [query, sources, dslText, queryBodyText, t]);

  const buildPayload = useCallback((): DashboardQuery => {
    return {
      ...query,
      dataSource: buildDataSource(),
    };
  }, [query, buildDataSource]);

  const buildProbePayload = useCallback((): DashboardQuery | null => {
    if (adaptorSource) {
      if (!query.dataSource?.adaptorId || !queryBodyText.trim()) {
        return null;
      }
      try {
        return { ...query, dataSource: buildDataSource() };
      } catch {
        return null;
      }
    }
    if (dslJsonError || !sources.some((source) => source.index?.trim())) {
      return null;
    }
    try {
      return { ...query, dataSource: buildDataSource() };
    } catch {
      return null;
    }
  }, [query, sources, dslText, dslJsonError, adaptorSource, queryBodyText, buildDataSource]);

  const onValidate = async () => {
    try {
      setErrors([]);
      const payload = buildPayload();
      const res = await dashboardQueryService.validate(payload);
      if (!res.valid) {
        setErrors(res.errors);
        return;
      }
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: { color: "success", icon: "check", title: t("query.validate"), content: "" },
        })
      );
    } catch (e: unknown) {
      setErrors([e instanceof Error ? e.message : String(e)]);
    }
  };

  const onProbe = useCallback(async () => {
    const payload = buildProbePayload();
    if (!payload) {
      setProbeError(
        adaptorSource ? t("query.probeNeedAdaptor") : t("query.dslPlaygroundNeedSource")
      );
      setProbeResult(null);
      return;
    }
    if (!adaptorSource && dslJsonError) {
      setProbeError(t("query.invalidJson"));
      setProbeResult(null);
      return;
    }
    setProbeLoading(true);
    setProbeError(null);
    try {
      const executeBody = buildWidgetExecuteRequest(sources, sourceDateRanges, date.range, dashboardTimezone());
      const res = await dashboardQueryService.probeDsl({
        query: payload,
        from: executeBody.from,
        to: executeBody.to,
        timezone: executeBody.timezone,
        params: executeBody.params,
        sourceRanges: executeBody.sourceRanges,
      });
      if (!res.valid) {
        setProbeError(res.errors.join("; "));
        setProbeResult(null);
        return;
      }
      setProbeResult(JSON.stringify(res.openSearchResponse ?? {}, null, 2));
    } catch (e: unknown) {
      setProbeError(e instanceof Error ? e.message : String(e));
      setProbeResult(null);
    } finally {
      setProbeLoading(false);
    }
  }, [buildProbePayload, sources, sourceDateRanges, date.range, dslJsonError, adaptorSource, t]);

  const onSave = async () => {
    try {
      setSaving(true);
      setErrors([]);
      const payload = buildPayload();
      const saved =
        mode === "edit" && id
          ? await dashboardQueryService.update(id, payload)
          : await dashboardQueryService.create(payload);
      if (embedded) {
        onSaved?.();
        onClose?.();
      } else {
        navigate("/widgets?tab=queries", { replace: true });
      }
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: saved.name || t("query.title"),
            content: "",
          },
        })
      );
    } catch (e: unknown) {
      setErrors([e instanceof Error ? e.message : String(e)]);
    } finally {
      setSaving(false);
    }
  };

  const form = (
      <PageListContainer>
        <Box sx={pageStyles.editRoot}>
          <PageCommandBar
            leading={
              <AppIconButton
                title={t("common.cancel")}
                onClick={goBack}
              >
                <ArrowBackOutlinedIcon fontSize="small" />
              </AppIconButton>
            }
            title={pageTitle}
            actions={
              <>
                <AppButton variant="outlined" color="primary" onClick={onValidate}>
                  {t("query.validate")}
                </AppButton>
                <AppButton variant="contained" color="primary" onClick={onSave} disabled={saving}>
                  {saving ? t("common.saving") : t("query.save")}
                </AppButton>
              </>
            }
          />

          {errors.length > 0 && (
            <Alert severity="error" sx={pageStyles.editErrorAlert}>
              {errors.join("; ")}
            </Alert>
          )}

          <PageSectionCard title={t("query.sectionGeneral")} description={t("query.sectionGeneralHint")}>
            <AppFormGrid>
              <AppFormField size={GRID_FULL}>
                <AppTextField
                  label={t("query.name")}
                  value={query.name || ""}
                  onChange={(e) => setQuery((q) => ({ ...q, name: e.target.value }))}
                  autoFocus
                />
              </AppFormField>
              <AppFormField size={GRID_FULL}>
                <AppTextField
                  label={t("query.description")}
                  value={query.description || ""}
                  onChange={(e) => setQuery((q) => ({ ...q, description: e.target.value }))}
                  multiline
                  minRows={2}
                />
              </AppFormField>
            </AppFormGrid>
          </PageSectionCard>

          <PageSectionCard
            title={t("query.sectionDataSource")}
            description={
              adaptorSource ? t("query.sectionDataSourceHintAdaptor") : t("query.sectionDataSourceHint")
            }
          >
            <WidgetQuerySourcePanel
              dataSource={query.dataSource}
              sources={sources}
              onDataSourceChange={(patch) =>
                setQuery((q) => ({ ...q, dataSource: { ...q.dataSource, ...patch } }))
              }
              onSourcesChange={setSources}
              dslText={dslText}
              onDslTextChange={setDslText}
              dslJsonError={dslJsonError}
              queryBodyText={queryBodyText}
              onQueryBodyTextChange={setQueryBodyText}
            />
            {adaptorSource ? (
              <Box sx={{ mt: 2 }}>
                <AppTextField
                  label={t("query.objectName")}
                  value={query.objectName || ""}
                  onChange={(e) => setQuery((q) => ({ ...q, objectName: e.target.value }))}
                  helperText={t("query.objectNameHint")}
                />
              </Box>
            ) : null}
          </PageSectionCard>

          <PageSectionCard title={t("query.sectionParameters")}>
            <WidgetParametersEditor
              parameters={query.dataSource?.parameters || DEFAULT_PARAMETERS}
              onChange={(parameters) =>
                setQuery((q) => ({
                  ...q,
                  dataSource: { ...q.dataSource, parameters },
                }))
              }
            />
          </PageSectionCard>

          <PageSectionCard
            title={t("query.sectionProbe")}
            description={adaptorSource ? t("query.sectionProbeHintAdaptor") : t("query.sectionProbeHint")}
          >
            {multiSource ? (
              <WidgetSourceDateRanges ranges={sourceDateRanges} onChange={setSourceDateRanges} disabled={probeLoading} />
            ) : (
              <DateRangeHeader
                range={date.range}
                preset={date.preset}
                onPresetChange={date.setPreset}
                customFrom={date.customFrom}
                customTo={date.customTo}
                onCustomFromChange={date.setCustomFrom}
                onCustomToChange={date.setCustomTo}
                onApplyCustom={date.applyCustom}
                label={date.label}
                onRefresh={onProbe}
                executing={probeLoading}
              />
            )}
            <Box sx={editorStyles.dslSection}>
              <Stack direction="row" sx={editorStyles.probeActionsRow}>
                <AppButton
                  variant="contained"
                  color="info"
                  size="small"
                  startIcon={
                    probeLoading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowOutlinedIcon />
                  }
                  disabled={probeLoading || !probeReady}
                  onClick={() => onProbe().catch(console.error)}
                >
                  {probeLoading ? t("query.probeRunning") : t("query.probe")}
                </AppButton>
                {adaptorSource ? (
                  query.dataSource?.adaptorId ? (
                    <Typography variant="caption" color="text.secondary">
                      {t("query.probeAdaptorReady")}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="warning.main">
                      {t("query.probeNeedAdaptor")}
                    </Typography>
                  )
                ) : primaryIndex ? (
                  <Typography variant="caption" color="text.secondary">
                    {t("query.dslPlaygroundIndex", { index: primaryIndex })}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="warning.main">
                    {t("query.dslPlaygroundNeedSource")}
                  </Typography>
                )}
              </Stack>
              {probeError ? <Alert severity="error" sx={editorStyles.probeErrorAlert}>{probeError}</Alert> : null}
              {probeResult ? (
                <Box sx={editorStyles.probeResultBox}>
                  <JsonCodeEditor
                    label={t("query.probeResult")}
                    value={probeResult}
                    onChange={() => undefined}
                    readOnly
                    height={320}
                  />
                </Box>
              ) : null}
            </Box>
          </PageSectionCard>
        </Box>
      </PageListContainer>
  );

  if (loading) {
    const loadingBody = (
      <Box sx={pageStyles.editLoadingBox}>
        <CircularProgress />
      </Box>
    );
    if (embedded) return loadingBody;
    return (
      <DashboardLayout>
        <DashboardNavbar />
        {loadingBody}
      </DashboardLayout>
    );
  }

  if (embedded) return form;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {form}
    </DashboardLayout>
  );
}
